import api from "../api/endpoints";

/**
 * Online payment (Razorpay).
 *
 * Uses Razorpay's Checkout script - the modal opens over the page - instead of
 * redirecting to the hosted payment link. A redirect threw the customer out of
 * the app mid-flow and brought them back on a callback URL that lost wherever
 * they were; the modal keeps them on the page and hands us the result directly.
 *
 * Trust boundary: the modal's success payload is not proof of payment. It goes
 * to /payments/verify, which recomputes the HMAC with the key secret
 * (server-side only) before anything is marked paid. The browser only ever sees
 * the public key id, which Razorpay requires to open checkout.
 */

const SCRIPT_SRC = "https://checkout.razorpay.com/v1/checkout.js";

/** Loads checkout.js once and resolves when window.Razorpay is available. */
const loadCheckout = () =>
  new Promise((resolve, reject) => {
    if (window.Razorpay) {
      resolve(window.Razorpay);
      return;
    }
    const existing = document.querySelector(`script[src="${SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener("load", () => resolve(window.Razorpay));
      existing.addEventListener("error", () => reject(new Error("Could not load Razorpay checkout.")));
      return;
    }
    const script = document.createElement("script");
    script.src = SCRIPT_SRC;
    script.async = true;
    script.onload = () => resolve(window.Razorpay);
    script.onerror = () => reject(new Error("Could not load Razorpay checkout. Check your connection."));
    document.body.appendChild(script);
  });

/** Opens the checkout modal and settles the payment. Resolves once verified. */
export const startOnlinePayment = async ({ refType, refId }) => {
  const res = await api.payments.initiate({ refType, refId, flow: "sdk" });
  const {
    paymentId, keyId, orderId, amountInPaise, currency, name, description, prefill,
  } = res.data;

  if (!keyId || !orderId) {
    throw new Error("Online payment is not configured. Please pay cash instead.");
  }

  const Razorpay = await loadCheckout();

  const result = await new Promise((resolve, reject) => {
    const rzp = new Razorpay({
      key: keyId,
      order_id: orderId,
      amount: amountInPaise,
      currency: currency || "INR",
      name: name || "KAAMWALA",
      description,
      prefill: {
        name: (prefill && prefill.name) || "",
        contact: (prefill && prefill.contact) || "",
        email: (prefill && prefill.email) || "",
      },
      theme: { color: "#1B4DE4" },
      handler: (r) => resolve(r),
      modal: {
        ondismiss: () => {
          const err = new Error("Payment cancelled.");
          err.cancelled = true;
          reject(err);
        },
      },
    });
    rzp.on("payment.failed", (e) => {
      reject(new Error((e && e.error && e.error.description) || "The payment could not be completed."));
    });
    rzp.open();
  }).catch(async (err) => {
    await cancelPayment(paymentId, err.message);
    throw err;
  });

  // Signature is verified on the server; only then is the order/trip settled.
  const verified = await api.payments.verify({
    paymentId,
    razorpayPaymentId: result.razorpay_payment_id,
    razorpaySignature: result.razorpay_signature,
  });

  return { paymentId, status: "paid", payment: verified.data };
};

/**
 * Polls until a payment settles. Kept for screens that resume while a webhook
 * is still in flight; checks immediately when the tab regains focus.
 */
export const waitForPayment = (paymentId, { intervalMs = 4000, timeoutMs = 8 * 60 * 1000, onUpdate } = {}) => {
  let timer = null;
  let settled = false;
  let finish = () => {};
  let check = async () => {};
  const startedAt = Date.now();

  // Declared out here so the visibility listener can reach it - it lives
  // outside the promise executor and cannot see anything declared inside.
  const onVisible = () => {
    if (document.visibilityState === "visible") check();
  };

  const promise = new Promise((resolve) => {
    finish = (result) => {
      if (settled) return;
      settled = true;
      clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      resolve(result);
    };

    check = async () => {
      if (settled) return;
      if (Date.now() - startedAt > timeoutMs) {
        finish({ status: "timeout" });
        return;
      }
      try {
        const res = await api.payments.status(paymentId);
        const { status } = res.data;
        if (onUpdate) onUpdate(status);
        if (status === "paid" || status === "failed" || status === "refunded") {
          finish({ status, payment: res.data });
        }
      } catch (err) {
        // Network hiccup - keep polling until the timeout.
      }
    };

    timer = setInterval(check, intervalMs);
    document.addEventListener("visibilitychange", onVisible);
    check();
  });

  promise.cancel = () => {
    settled = true;
    clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisible);
  };
  return promise;
};

/** Hosted page, kept as a fallback - the server still supports the 'link' flow. */
export const startHostedPayment = async ({ refType, refId }) => {
  const res = await api.payments.initiate({ refType, refId, flow: "link" });
  const { paymentId, paymentUrl, amount } = res.data;
  if (!paymentUrl) throw new Error("The payment page could not be opened. Please try again.");
  window.location.href = paymentUrl;
  return { paymentId, paymentUrl, amount };
};

/** Tells the server an attempt was abandoned so it does not sit pending forever. */
export const cancelPayment = async (paymentId, reason = "Cancelled by user") => {
  try {
    await api.payments.markFailed({ paymentId, reason });
  } catch (e) {
    /* best effort */
  }
};

const payment = { startOnlinePayment, startHostedPayment, waitForPayment, cancelPayment };
export default payment;
